<template>
  <button
    type="button"
    :disabled="isDisabled"
    :class="[
      'UIButtonIcon',
      `UIButtonIcon--theme-${theme}`,
      `UIButtonIcon--size-${size}`,
      {
        'UIButtonIcon--disabled': isDisabled,
        'UIButtonIcon--square': isSquare,
      },
    ]"
  >
    <slot>
      <UIIcon v-if="icon" class="UIButtonIcon__icon" :name="icon" />
    </slot>
  </button>
</template>

<script lang="ts" setup>
import UIIcon from '../icons/UIIcon.vue'

import type { IIcons } from '../icons/types'

withDefaults(
  defineProps<{
    /**
     * Glyph from the local icon set. Optional so a caller can supply its own icon
     * through the default slot instead — pass `--size` / `--color` down to it via
     * this button's `--icon-size` / `--icon-color`, which the built-in glyph reads.
     */
    icon?: IIcons
    isDisabled?: boolean
    theme?: 'default' | 'white' | 'gray'
    size?: 'md' | 'lg'
    isSquare?: boolean
  }>(),
  {
    isDisabled: false,
    theme: 'default',
    size: 'md',
    isSquare: false,
  },
)
</script>

<style scoped lang="scss">
.UIButtonIcon {
  @include trans(0.2s);
  --icon-size: var(--size, 1rem);
  --icon-color: var(--color);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &.UIButtonIcon--size-md {
    --hover-size: 1.5rem;
  }
  &.UIButtonIcon--size-lg {
    --hover-size: 2.5rem;
  }

  &.UIButtonIcon--disabled {
    pointer-events: none;
    background-color: #{$color-neutral-10} !important;
    --color: #{$color-neutral-50} !important;
  }

  &:hover {
    // --icon-color: #{$color-primary-50};

    &:before {
      opacity: 1;
      height: calc(var(--icon-size) + 0.5rem);
      width: calc(var(--icon-size) + 0.5rem);
    }
  }

  .UIButtonIcon__icon {
    --size: var(--icon-size);
    --color: var(--icon-color);
    z-index: 1;
  }

  &--theme-default {
    &:before {
      position: absolute;
      height: 20px;
      width: 20px;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      transform-origin: 50% 50%;
      content: '';
      background-color: $color-neutral-10;
      border-radius: 50%;
      opacity: 0;
      transition: all 0.3s;
    }

    &:hover {
      // --icon-color: #{$color-primary-50};

      &:before {
        opacity: 1;
        height: var(--hover-size);
        width: var(--hover-size);
      }
    }
  }

  &--square {
    &.UIButtonIcon--theme-default {
      &:before {
        border-radius: 4px;
      }
    }
  }

  &--theme-white {
    background-color: $color-white;
    padding: 0.25rem;
    border-radius: 0.25rem;

    &:hover {
      background-color: $color-neutral-5;
      box-shadow: 0px 2px 5px 0px #0000000d;
    }
    &:active {
      background-color: $color-neutral-20;
    }
  }

  &--theme-gray {
    background-color: $color-neutral-10;
    padding: 4px;
    border-radius: 0.25rem;

    &:hover {
      background-color: $color-neutral-20;
      box-shadow: 0px 2px 5px 0px #0000000d;
    }
    &:active {
      background-color: $color-neutral-30;
    }
  }
}
</style>
